import { fileToBase64 } from './storage';
import { pdfToImage, isPDF, extractTextFromPDF } from '../utils/pdf-to-image';
import { parseInvoiceFromText } from './invoice-parser';

// 阿里云函数 API 地址
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://sunray-t-system-kweoytfwwx.cn-chengdu.fcapp.run';

/**
 * Invoice OCR Result Interface
 */
export interface InvoiceOCRResult {
  invoiceNumber: string;
  amount: number;
  date: string;
  invoiceType: string;
  taxAmount?: number;
  buyerName?: string;
  sellerName?: string;
  confidence?: number;
}

/**
 * 解析中文大写金额为数字
 * 例如："柒拾捌圆整" -> 78
 */
function parseCapitalAmount(capitalStr: string): number {
  if (!capitalStr || typeof capitalStr !== 'string') return 0;

  // 移除常见的前缀和后缀
  capitalStr = capitalStr.replace(/^[¥￥]\s*/, '').replace(/[整正]$/, '');

  // 尝试直接提取数字（如果大写金额实际包含阿拉伯数字）
  const numMatch = capitalStr.match(/\d+\.?\d*/);
  if (numMatch) {
    return parseFloat(numMatch[0]);
  }

  // 中文数字映射
  const digitMap: Record<string, number> = {
    '零': 0, '壹': 1, '贰': 2, '叁': 3, '肆': 4,
    '伍': 5, '陆': 6, '柒': 7, '捌': 8, '玖': 9
  };

  const unitMap: Record<string, number> = {
    '拾': 10, '佰': 100, '仟': 1000, '万': 10000,
    '十': 10, '百': 100, '千': 1000,
    '角': 0.1, '分': 0.01
  };

  let result = 0;
  let currentNum = 0;
  let temp = 0;

  // 分离整数和小数部分
  const parts = capitalStr.split(/[元圆圓]/);
  const integerPart = parts[0] || '';
  const decimalPart = parts[1] || '';

  // 解析整数部分
  for (let i = 0; i < integerPart.length; i++) {
    const char = integerPart[i];

    if (digitMap.hasOwnProperty(char)) {
      currentNum = digitMap[char];
    } else if (unitMap.hasOwnProperty(char)) {
      const unit = unitMap[char];
      if (unit >= 10) {
        if (currentNum === 0) currentNum = 1; // 处理"十"开头的情况
        temp += currentNum * unit;
        currentNum = 0;

        if (unit === 10000) { // 万
          result += temp;
          temp = 0;
        }
      }
    }
  }
  result += temp + currentNum;

  // 解析小数部分
  let decimal = 0;
  for (let i = 0; i < decimalPart.length; i++) {
    const char = decimalPart[i];
    if (digitMap.hasOwnProperty(char)) {
      const nextChar = decimalPart[i + 1];
      if (nextChar === '角') {
        decimal += digitMap[char] * 0.1;
      } else if (nextChar === '分') {
        decimal += digitMap[char] * 0.01;
      }
    }
  }

  return Math.round((result + decimal) * 100) / 100;
}

/**
 * 调用阿里云函数进行 OCR 识别
 */
async function callOCRApi(imageBase64: string): Promise<any> {
  const response = await fetch(`${API_BASE_URL}/api/ocr`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ imageBase64 }),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: '网络请求失败' }));
    throw new Error(error.error || `HTTP ${response.status}`);
  }

  const result = await response.json();
  if (!result.success) {
    throw new Error(result.error || 'OCR 识别失败');
  }

  return result.data;
}

/**
 * 通过阿里云函数识别发票
 */
export const recognizeInvoice = async (file: File): Promise<InvoiceOCRResult> => {
  try {
    console.log('🔍 开始识别发票...');
    console.log('文件类型:', file.type);
    console.log('文件名:', file.name);
    console.log('文件大小:', (file.size / 1024).toFixed(2), 'KB');

    let processedFile = file;

    // 如果是 PDF，优先尝试文本提取（更快更准确）
    if (isPDF(file)) {
      console.log('📄 检测到 PDF 文件，尝试直接提取文本...');
      try {
        const text = await extractTextFromPDF(file);
        if (text && text.length > 50) {
          console.log('✅ PDF 文本提取成功！');
          const parsed = parseInvoiceFromText(text);

          // 检查是否成功提取到关键信息
          // 1. 发票号必须是 15-20 位纯数字
          // 2. 金额必须大于 0 且小于 100 万（超大金额可能是解析错误）
          const isValidNumber = parsed.invoiceNumber &&
            /^\d{15,20}$/.test(parsed.invoiceNumber);
          const isValidAmount = parsed.amount > 0 && parsed.amount < 1000000;

          if (isValidNumber && isValidAmount) {
            console.log('✅ 文本解析成功！发票号和金额都已提取');
            console.log(`   发票号: ${parsed.invoiceNumber}, 金额: ${parsed.amount}`);
            return parsed;
          } else {
            console.log('⚠️ PDF 文本提取不完整，缺少关键字段：');
            console.log(`   发票号: ${parsed.invoiceNumber || '无'}`);
            console.log(`   金额: ${parsed.amount || '0'}`);
            console.log('   自动切换到 OCR 识别方式...');
          }
        } else {
          console.log('⚠️ PDF 文本内容太少，尝试OCR方式...');
        }
      } catch (error) {
        console.warn('⚠️ PDF 文本提取失败，尝试OCR方式...', error);
      }

      // 如果文本提取失败，先将 PDF 转为图片再用 OCR
      console.log('📄 将 PDF 转换为图片后进行 OCR 识别...');

      // 先将 PDF 转为图片（腾讯云 OCR 不支持直接识别 PDF）
      const imageFile = await pdfToImage(file);
      const imageBase64 = await fileToBase64(imageFile);
      console.log(`✅ PDF 转图片成功 (图片大小: ${(imageFile.size / 1024).toFixed(2)} KB)`);

      // 调用阿里云函数
      console.log('📡 调用阿里云函数 OCR API (PDF转图片模式)');
      const result = await callOCRApi(imageBase64);

      console.log('✅ OCR 识别成功:', result);

      return processOCRResult(result);
    }

    // 如果不是PDF，走正常的图片识别流程
    // 检查是否是图片
    if (!processedFile.type.startsWith('image/')) {
      throw new Error('不支持的文件类型，请上传图片或PDF格式的发票');
    }

    // 转换为 Base64
    const base64Image = await fileToBase64(processedFile);
    console.log(`✅ Base64 转换成功 (长度: ${base64Image.length})`);

    // 调用阿里云函数
    console.log('📡 调用阿里云函数 OCR API (图片模式)');
    const result = await callOCRApi(base64Image);

    console.log('✅ OCR 识别成功:', result);

    return processOCRResult(result);
  } catch (error: any) {
    console.error('❌ OCR 识别失败:', error);
    throw error;
  }
};

/**
 * 处理 OCR 结果
 */
function processOCRResult(result: any): InvoiceOCRResult {
  // 处理发票号：移除 "No" 前缀
  let cleanInvoiceNumber = result.number || result.invoiceNumber || '';
  if (cleanInvoiceNumber) {
    cleanInvoiceNumber = cleanInvoiceNumber.replace(/^[Nn][Oo]\.?\s*/g, '');
  }

  // 处理金额
  let finalAmount = result.amount || 0;

  return {
    invoiceNumber: cleanInvoiceNumber,
    amount: finalAmount,
    date: result.date || new Date().toISOString().split('T')[0],
    invoiceType: result.type || result.invoiceType || '增值税发票',
    taxAmount: result.taxAmount || 0,
    buyerName: result.buyer || result.buyerName || '',
    sellerName: result.seller || result.sellerName || '',
    confidence: result.confidence || 0.95
  };
}
